import { Box, useTheme } from "@mui/material";
import { StatisticCard } from "./StatisticCard";
import { StatCardType } from "./MyMessagesTypes";
import { useMemo } from "react";
import { useTimeContext } from "@/context/TimeContext";
import { useTaskerMessageContext } from "../TaskerMessageContext";
import PendingIcon from "@mui/icons-material/Pending";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { AddCircle } from "@mui/icons-material";

export const StatsCards = () => {
  const theme = useTheme();
  const { time } = useTimeContext();
  const { taskerMyMessages } = useTaskerMessageContext();

  const firstWeekMessages = useMemo(() => {
    const today = time.startOf("day");
    const sevenDaysAgo = today.minus({ days: 6 }).startOf("day");
    return taskerMyMessages.filter((message) => {
      const messageDateTime = message.timestamp.setZone("local");
      return messageDateTime >= sevenDaysAgo;
    });
  }, [taskerMyMessages]);

  const statCards = useMemo(() => {
    const pendingMessages = firstWeekMessages.filter(
      (message) => !message.reviewStatus || message.reviewStatus === "pending",
    ).length;

    const approvedMessages = firstWeekMessages.filter(
      (message) => message.reviewStatus === "approved",
    );

    const rejectedMessages = firstWeekMessages.filter(
      (message) => message.reviewStatus === "rejected",
    ).length;

    const pointsRewarded = approvedMessages.reduce((total, message) => {
      if (message.reviewStatus === "approved" && message.rewardOptions) {
        return (
          total +
          message.rewardOptions.reduce((sum, option) => sum + option.amount, 0)
        );
      }
      return total;
    }, 0);

    const cards: StatCardType[] = [
      {
        title: "Approved Messages",
        value: approvedMessages.length,
        description: "Successfully approved messages",
        icon: <CheckCircleIcon sx={{ fontSize: 30 }} />,
        color: theme.palette.success.main,
        total: firstWeekMessages.length,
      },
      {
        title: "Rejected Messages",
        value: rejectedMessages,
        description: "Messages that were rejected",
        icon: <CancelIcon sx={{ fontSize: 30 }} />,
        color: theme.palette.error.main,
        total: firstWeekMessages.length,
      },
      {
        title: "Messages Pending",
        value: pendingMessages,
        description: "Messages awaiting review",
        icon: <PendingIcon sx={{ fontSize: 30 }} />,
        color: theme.palette.warning.main,
        total: firstWeekMessages.length,
      },
      {
        title: "Points Rewarded",
        value: pointsRewarded,
        description: "Total points earned",
        icon: <AddCircle sx={{ fontSize: 30 }} />,
        color: theme.palette.primary.main,
        total: firstWeekMessages.length,
      },
    ];

    return cards;
  }, [firstWeekMessages, theme]);

  return (
    <Box
      sx={{
        mb: { xs: 2.5, sm: 3 },
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(2, 1fr)",
          md: "repeat(4, 1fr)",
        },
        gap: { xs: 1.5, sm: 2 },
      }}
    >
      {statCards.map((stat) => (
        <StatisticCard key={stat.title} {...stat} />
      ))}
    </Box>
  );
};
